import { LoggingService } from '@adaptiva/core/logging';
import { MenuControllerId, MenuItem } from '@adaptiva/core/menus';
import { EJavaClassNames, ServerObjectMenuController } from '@adaptiva/data-service';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { LlmConversation } from '../classes';
import { AidaService, LlmConversationService } from '../services';

@Injectable()
export class LlmConversationMenuController extends ServerObjectMenuController<LlmConversation> {

	public static override readonly id: MenuControllerId.Any = `${MenuControllerId.Prefix.Default}${LlmConversation.className}`;

	public readonly openMenuName: string = 'Open Chat';

	public override readonly deleteMenuName: string = 'Delete Chat';

	public override readonly renameMenuName: string = 'Rename Chat';

	protected readonly loggingService: LoggingService = inject(LoggingService);

	protected readonly llmConversations: LlmConversationService = inject(LlmConversationService);

	protected readonly aida: AidaService = inject(AidaService);

	public override getClassName(): string {
		return EJavaClassNames.LlmConversation;
	}

	public override generate(_conversation: LlmConversation): Observable<MenuItem<LlmConversation>[]> {
		return of([
			this.getOpenChatMenuItem(_conversation),
			this.getRenameMenuItem(_conversation),
			this.getDeleteMenuItem(_conversation),
		]);
	}

	public override getRenameMenuItem(_conversation: LlmConversation): MenuItem<LlmConversation> {
		const _itemId: string = this.hashItem(this.renameMenuName, _conversation);
		const _item: MenuItem<LlmConversation> = this.getItem(_itemId, _conversation) || this.createItem({
			id: _itemId,
			name: this.renameMenuName,
			data: _conversation,
			isDisabled: new BehaviorSubject(true),
			onSelect: this.rename.bind(this)
		});

		this.enableRename(_item);

		return _item;
	}

	protected rename(_item: MenuItem<LlmConversation>): void {
		this.llmConversations.renameChat(_item.data).subscribe({
			error: (e: Error) => {
				this.loggingService.defaultErrorHandler('LlmConversationMenuController.onRenameClick', e);
			}
		});
	}

	public override getDeleteMenuItem(_conversation: LlmConversation): MenuItem<LlmConversation> {
		const _itemId: string = this.hashItem(this.deleteMenuName, _conversation);
		const _item: MenuItem<LlmConversation> = this.getItem(_itemId, _conversation) || this.createItem({
			id: _itemId,
			name: this.deleteMenuName,
			data: _conversation,
			isDisabled: new BehaviorSubject(false),
			onSelect: this.delete.bind(this)
		});

		return _item;
	}

	protected delete(_item: MenuItem<LlmConversation>): void {
		this.llmConversations.deleteChat(_item.data).subscribe({
			error: (e: Error) => {
				this.loggingService.defaultErrorHandler('LlmConversationMenuController.delete', e);
			}
		});
	}

	public getOpenChatMenuItem(_conversation: LlmConversation): MenuItem<LlmConversation> {
		const _itemId: string = this.hashItem(this.openMenuName, _conversation);
		const _item: MenuItem<LlmConversation> = this.getItem(_itemId, _conversation) || this.createItem({
			id: _itemId,
			name: this.openMenuName,
			data: _conversation,
			onSelect: this.open.bind(this)
		});

		return _item;
	}

	protected open(_item: MenuItem<LlmConversation>): void {
		this.llmConversations.open(_item.data);
	}

}

